/**
 * Autor: CI Sistemas de Informática Ltda.
 * Data: 2026-06-16
 * Descrição: Página de relatórios do sistema de estacionamento.
 *            Mantém todas as abas originais (Relatório Diário, Ocupação, Atividade de Câmeras)
 *            e adiciona as novas abas: Pátio Atual e Relatório por Período.
 *            O relatório de pátio é atualizado automaticamente a cada 30 segundos.
 *            O relatório por período permite filtrar por data, horário e tipo de movimento.
 *            Adicionado suporte a evidências fotográficas nas tabelas de Pátio e Por Período.
 *            Paginação com seletor de registros por página (padrão: 20) em Pátio Atual e Por Período.
 *            Ordenação crescente/decrescente por data/hora de entrada no Pátio Atual.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import {
  FiRefreshCw, FiClock, FiTruck, FiSearch, FiCamera,

  FiArrowUp, FiArrowDown, FiDollarSign, FiCreditCard, FiDownload
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { hojeLocal, diasAtrasLocal } from '../utils/dataLocal'; // Onda 138: data local, não UTC
import Pagination from '../components/Pagination';
import { useAuth } from '../context/AuthContext';
import '../styles/Reports.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return '—';
  const m = parseInt(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}min` : `${h}h`;
};

const today = hojeLocal();
// Onda 40: o mês corrente é o padrão útil para a frequência por veículo — um
// único dia quase nunca tem repetição, e o operador teria de trocar a data antes
// de qualquer consulta valer alguma coisa.
const firstDayOfMonth = `${today.slice(0, 7)}-01`;
const fmtMoney = (value) => Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDateTime = (dt) => (dt ? new Date(dt).toLocaleString('pt-BR') : '—');
const fmtMinutes = (m) => formatDuration(m);

// Helper: monta URL do endpoint de evidência com token
function evidenceUrl(imagePath) {
  if (!imagePath) return null;
  const token = localStorage.getItem('token');
  return `/api/evidence/${imagePath}?token=${encodeURIComponent(token)}`;
}

// ─── Cabeçalho de coluna ordenável ────────────────────────────────────────────
const SortableHeader = ({ label, field, sortField, sortDir, onSort }) => {
  const isActive = sortField === field;
  return (
    <th
      className={`sortable-header ${isActive ? 'sort-active' : ''}`}
      onClick={() => onSort(field)}
      title={`Ordenar por ${label} (${isActive && sortDir === 'desc' ? 'crescente' : 'decrescente'})`}
    >
      <span className="sortable-header-inner">
        {label}
        <span className="sort-icon">
          {isActive
            ? (sortDir === 'asc' ? <FiArrowUp size={13} /> : <FiArrowDown size={13} />)
            : <span className="sort-icon-neutral">↕</span>
          }
        </span>
      </span>
    </th>
  );
};

const Reports = () => {
  const { can } = useAuth();
  const [reportType, setReportType] = useState('yard');
  const [loading, setLoading] = useState(false);

  // Estado do modal de evidência
  const [evidenceModal, setEvidenceModal] = useState(null);

  // Estados por relatório
  const [dailyReport,     setDailyReport]     = useState(null);
  const [occupancyReport, setOccupancyReport] = useState(null);
  const [cameraReport,    setCameraReport]    = useState(null);
  const [yardReport,      setYardReport]      = useState(null);
  const [periodReport,    setPeriodReport]    = useState(null);
  const [financialReport, setFinancialReport] = useState(null);
  const [cashierReport,   setCashierReport]   = useState(null);
  const [monthlyPortfolio, setMonthlyPortfolio] = useState(null);
  // Onda 40: frequência por veículo, paginada no servidor
  const [frequencyReport, setFrequencyReport] = useState(null);
  // Onda 46: auditoria das vistorias de pátio
  const [inventoryReport, setInventoryReport] = useState(null);
  // Onda 108: `base` diz o que o mínimo N compara — estadias ou dias distintos
  // com acesso. No estacionamento o padrão continua sendo estadias, que é o que
  // esta tela sempre contou.
  const [freqFilters, setFreqFilters] = useState({
    start_date: firstDayOfMonth, end_date: today,
    min_visits: 2, vehicle_type: '', base: 'acessos', limit: 50, offset: 0,
  });

  // Filtros
  const [selectedDate, setSelectedDate]     = useState(today);
  const [lastYardUpdate, setLastYardUpdate] = useState(null);
  // Ocupação, Câmeras e Financeiro respondiam sobre janelas fixas e diferentes
  // entre si: sete dias, a base inteira e um único dia. Passam a compartilhar um
  // intervalo escolhido pelo operador.
  const [rangeFilters, setRangeFilters] = useState({
    start_date: diasAtrasLocal(6),
    end_date: today,
  });
  const [periodFilters, setPeriodFilters]   = useState({
    start_date: today,
    end_date:   today,
    start_time: '00:00',
    end_time:   '23:59',
    log_type:   'ambos'
  });

  // ── Paginação — Pátio Atual ────────────────────────────────────────────────
  const [yardPage, setYardPage]           = useState(1);
  const [yardPageSize, setYardPageSize]   = useState(20);

  // ── Ordenação — Pátio Atual ────────────────────────────────────────────────
  const [yardSortField, setYardSortField] = useState('entry_time');
  const [yardSortDir,   setYardSortDir]   = useState('desc'); // desc = mais recente primeiro

  // ── Paginação — Por Período ────────────────────────────────────────────────
  const [periodPage, setPeriodPage]         = useState(1);
  const [periodPageSize, setPeriodPageSize] = useState(20);

  const fetchReport = useCallback(async (type) => {
    setLoading(true);
    try {
      if (type === 'daily') {
        const r = await api.get(`/api/reports/daily?date=${selectedDate}`);
        setDailyReport(r.data);
      } else if (type === 'occupancy') {
        const r = await api.get(`/api/reports/occupancy?start_date=${rangeFilters.start_date}&end_date=${rangeFilters.end_date}`);
        setOccupancyReport(r.data);
      } else if (type === 'camera') {
        const r = await api.get(`/api/reports/camera-activity?start_date=${rangeFilters.start_date}&end_date=${rangeFilters.end_date}`);
        setCameraReport(r.data);
      } else if (type === 'yard') {
        const r = await api.get('/api/reports/yard');
        setYardReport(r.data);
        setLastYardUpdate(new Date());
        setYardPage(1); // Volta para página 1 ao atualizar
      } else if (type === 'financial') {
        const r = await api.get(`/api/reports/financial?start_date=${rangeFilters.start_date}&end_date=${rangeFilters.end_date}`);
        setFinancialReport(r.data);
      } else if (type === 'cashier') {
        const r = await api.get(`/api/reports/cashier?start_date=${rangeFilters.start_date}&end_date=${rangeFilters.end_date}`);
        setCashierReport(r.data);
      } else if (type === 'monthly') {
        const r = await api.get('/api/reports/monthly-portfolio');
        setMonthlyPortfolio(r.data);
      } else if (type === 'inventory') {
        const r = await api.get(`/api/reports/inventory?start_date=${rangeFilters.start_date}&end_date=${rangeFilters.end_date}`);
        setInventoryReport(r.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, rangeFilters]);

  useEffect(() => {
    if (reportType !== 'period' && reportType !== 'frequency') fetchReport(reportType);
  }, [reportType, fetchReport]);

  // Atualização automática do pátio a cada 30s
  useEffect(() => {
    if (reportType !== 'yard') return;
    const interval = setInterval(() => fetchReport('yard'), 30000);
    return () => clearInterval(interval);
  }, [reportType, fetchReport]);

  // Onda 40: a consulta é explícita, como na aba Por Período. Trocar de aba não
  // dispara nada, porque o agrupamento por placa é caro em períodos longos.
  const fetchFrequencyReport = async (filtros = freqFilters) => {
    setLoading(true);
    try {
      const p = new URLSearchParams({
        start_date: filtros.start_date, end_date: filtros.end_date,
        min_visits: String(filtros.min_visits), base: filtros.base || 'acessos',
        limit: String(filtros.limit), offset: String(filtros.offset),
      });
      if (filtros.vehicle_type) p.append('vehicle_type', filtros.vehicle_type);
      const r = await api.get(`/api/reports/plate-frequency?${p.toString()}`);
      setFrequencyReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao levantar a frequência por veículo');
    } finally { setLoading(false); }
  };

  const exportFrequencyCsv = async () => {
    try {
      const p = new URLSearchParams({
        start_date: freqFilters.start_date, end_date: freqFilters.end_date,
        min_visits: String(freqFilters.min_visits), base: freqFilters.base || 'acessos',
      });
      if (freqFilters.vehicle_type) p.append('vehicle_type', freqFilters.vehicle_type);
      const r = await api.get(`/api/reports/plate-frequency/export.csv?${p.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `frequencia_${freqFilters.start_date}_${freqFilters.end_date}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erro ao exportar o CSV');
    }
  };

  const fetchPeriodReport = async (pagina = 1, tamanho = periodPageSize) => {
    setLoading(true);
    try {
      const { start_date, end_date, start_time, end_time, log_type } = periodFilters;
      const r = await api.get(
        `/api/reports/period?start_date=${start_date}&end_date=${end_date}` +
        `&start_time=${start_time}&end_time=${end_time}&log_type=${log_type}` +
        `&limit=${tamanho}&offset=${(pagina - 1) * tamanho}`
      );
      setPeriodReport(r.data);
      setPeriodPage(pagina);
    } catch (error) {
      toast.error('Erro ao gerar relatório por período');
    } finally {
      setLoading(false);
    }
  };

  // ── Ordenação e paginação do Pátio Atual ───────────────────────────────────
  const handleYardSort = (field) => {
    if (yardSortField === field) {
      setYardSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setYardSortField(field);
      setYardSortDir('desc');
    }
    setYardPage(1);
  };

  const sortedYardVehicles = useMemo(() => {
    if (!yardReport?.vehicles) return [];
    const arr = [...yardReport.vehicles];
    arr.sort((a, b) => {
      let valA, valB;
      if (yardSortField === 'entry_time') {
        valA = new Date(a.entry_time).getTime();
        valB = new Date(b.entry_time).getTime();
      } else if (yardSortField === 'minutes_in_yard') {
        valA = a.minutes_in_yard || 0;
        valB = b.minutes_in_yard || 0;
      } else if (yardSortField === 'plate') {
        valA = a.plate || '';
        valB = b.plate || '';
        return yardSortDir === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      return yardSortDir === 'asc' ? valA - valB : valB - valA;
    });
    return arr;
  }, [yardReport, yardSortField, yardSortDir]);

  const yardTotalRecords  = sortedYardVehicles.length;
  const yardTotalPages    = Math.max(1, Math.ceil(yardTotalRecords / yardPageSize));
  const paginatedYard     = sortedYardVehicles.slice(
    (yardPage - 1) * yardPageSize,
    yardPage * yardPageSize
  );

  // ── Paginação do Por Período ───────────────────────────────────────────────
  // A página vem pronta do servidor. Antes, a tela recebia o período inteiro e
  // fatiava no navegador: um mês de leituras trafegava a cada consulta e, depois
  // que a rota ganhou teto, o operador perderia registros sem aviso nenhum.
  const periodRecords      = periodReport?.records || [];
  const periodTotalRecords = periodReport?.totals?.total || 0;
  const periodTotalPages   = Math.max(1, Math.ceil(periodTotalRecords / periodPageSize));
  const paginatedPeriod    = periodRecords;

  /** O que o sistema fez com a leitura: abriu ticket, fechou, ou nada — e por quê. */
  const MOTIVO_SEM_USO = {
    duplicada: 'repetição da mesma passagem',
    fora_da_zona: 'fora da zona de leitura',
    sem_ticket: 'nenhum ticket correspondente',
    ticket_recente: 'ticket aberto há pouco',
    saida_recente: 'saída registrada há pouco',
    filtrada: 'descartada pelo filtro de placas',
    observacao: 'zona de observação',
  };

  const renderUso = (r) => {
    if (r.ticket_role === 'abertura') {
      return <span className="usage-badge abertura" title={`Abriu o ticket #${r.ticket_id}`}>abriu #{r.ticket_id}</span>;
    }
    if (r.ticket_role) {
      return <span className="usage-badge baixa" title={`Fechou o ticket #${r.ticket_id}`}>baixou #{r.ticket_id}</span>;
    }
    const motivo = MOTIVO_SEM_USO[r.unused_reason] || r.unused_reason;
    return <span className="usage-badge sem-uso" title={motivo || 'Sem registro associado'}>{motivo || '—'}</span>;
  };

  // ── Abas de navegação ──────────────────────────────────────────────────────
  const tabs = [
    { id: 'yard',      label: '🅿 Pátio Atual' },
    { id: 'period',    label: '🔍 Por Período' },
    { id: 'daily',     label: '📅 Diário' },
    { id: 'occupancy', label: '📊 Ocupação' },
    { id: 'camera',    label: '📷 Câmeras' },
    { id: 'financial', label: '💰 Financeiro' },
    { id: 'cashier',   label: '🧾 Caixas' },
    { id: 'monthly',   label: '📋 Mensalidades' },
    { id: 'frequency', label: '🔁 Frequência por Veículo' },
    // A auditoria só existe para quem alcança o próprio inventário: mostrar a
    // aba a quem receberia 403 ao abri-la seria uma promessa falsa.
    ...(can('yard_inventory') ? [{ id: 'inventory', label: '📋 Inventário do Pátio' }] : []),
  ];

  // ── Pátio Atual ────────────────────────────────────────────────────────────
  const renderYardReport = () => {
    if (!yardReport) return null;
    const { total } = yardReport;
    return (
      <div className="report-content">
        <div className="yard-header">
          <div className="yard-title-row">
            <h2>Veículos no Pátio Agora</h2>
            <button className="btn btn-sm btn-outline-primary" onClick={() => fetchReport('yard')} disabled={loading}>
              <FiRefreshCw className={loading ? 'spin' : ''} /> Atualizar
            </button>
          </div>
          {lastYardUpdate && (
            <p className="yard-update-time">
              <FiClock size={12} /> Atualizado às {lastYardUpdate.toLocaleTimeString('pt-BR')} — atualiza automaticamente a cada 30s
            </p>
          )}
        </div>
        <div className="yard-summary">
          <div className="yard-stat">
            <FiTruck size={28} />
            <div>
              <span className="yard-stat-value">{total}</span>
              <span className="yard-stat-label">veículo{total !== 1 ? 's' : ''} no pátio</span>
            </div>
          </div>
        </div>
        {total === 0 ? (
          <div className="yard-empty">
            <FiTruck size={40} style={{ opacity: 0.3 }} />
            <p>Nenhum veículo no pátio no momento</p>
          </div>
        ) : (
          <>
            <Pagination
              currentPage={yardPage}
              totalPages={yardTotalPages}
              pageSize={yardPageSize}
              totalRecords={yardTotalRecords}
              onPageChange={setYardPage}
              onPageSizeChange={(s) => { setYardPageSize(s); setYardPage(1); }}
            />

            <table className="report-table">
              <thead>
                <tr>
                  <th>#</th>
                  <SortableHeader
                    label="Placa"
                    field="plate"
                    sortField={yardSortField}
                    sortDir={yardSortDir}
                    onSort={handleYardSort}
                  />
                  <SortableHeader
                    label="Entrada"
                    field="entry_time"
                    sortField={yardSortField}
                    sortDir={yardSortDir}
                    onSort={handleYardSort}
                  />
                  <SortableHeader
                    label="Permanência"
                    field="minutes_in_yard"
                    sortField={yardSortField}
                    sortDir={yardSortDir}
                    onSort={handleYardSort}
                  />
                  <th>Câmera de Entrada</th>
                  <th title="Evidência fotográfica de entrada">📷</th>
                </tr>
              </thead>
              <tbody>
                {paginatedYard.map((v, idx) => {
                  const isLong = v.minutes_in_yard > 240;
                  const globalIdx = (yardPage - 1) * yardPageSize + idx + 1;
                  return (
                    <tr key={v.ticket_id} className={isLong ? 'row-warning' : ''}>
                      <td>{globalIdx}</td>
                      <td><span className="plate-badge">{v.plate}</span></td>
                      <td>{new Date(v.entry_time).toLocaleString('pt-BR')}</td>
                      <td><span className={`duration-badge ${isLong ? 'duration-long' : ''}`}>{formatDuration(v.minutes_in_yard)}</span></td>
                      <td>{v.entry_camera_name}</td>
                      <td className="evidence-cell">
                        {v.entry_image_path ? (
                          <button
                            className="btn-evidence"
                            title="Ver evidência fotográfica de entrada"
                            onClick={() => setEvidenceModal({
                              url:   evidenceUrl(v.entry_image_path),
                              plate: v.plate,
                              type:  'Entrada'
                            })}
                          >
                            <FiCamera size={15} />
                          </button>
                        ) : (
                          <span className="evidence-none" title="Sem evidência fotográfica">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <Pagination
              currentPage={yardPage}
              totalPages={yardTotalPages}
              pageSize={yardPageSize}
              totalRecords={yardTotalRecords}
              onPageChange={setYardPage}
              onPageSizeChange={(s) => { setYardPageSize(s); setYardPage(1); }}
            />
          </>
        )}
      </div>
    );
  };

  // ── Relatório por Período ──────────────────────────────────────────────────
  const renderPeriodReport = () => (
    <div className="report-content">
      <h2>Relatório por Período e Horário</h2>

      {/* Filtros */}
      <div className="period-filters">
        <div className="filter-group">
          <label>Data inicial</label>
          <input type="date" value={periodFilters.start_date}
            onChange={e => setPeriodFilters(p => ({ ...p, start_date: e.target.value }))} />
        </div>
        <div className="filter-group">
          <label>Data final</label>
          <input type="date" value={periodFilters.end_date}
            onChange={e => setPeriodFilters(p => ({ ...p, end_date: e.target.value }))} />
        </div>
        <div className="filter-group">
          <label>Horário inicial</label>
          <input type="time" value={periodFilters.start_time}
            onChange={e => setPeriodFilters(p => ({ ...p, start_time: e.target.value }))} />
        </div>
        <div className="filter-group">
          <label>Horário final</label>
          <input type="time" value={periodFilters.end_time}
            onChange={e => setPeriodFilters(p => ({ ...p, end_time: e.target.value }))} />
        </div>
        <div className="filter-group">
          <label>Tipo de movimento</label>
          <select value={periodFilters.log_type}
            onChange={e => setPeriodFilters(p => ({ ...p, log_type: e.target.value }))}>
            <option value="ambos">Entradas e Saídas</option>
            <option value="entrada">Somente Entradas</option>
            <option value="saida">Somente Saídas</option>
          </select>
        </div>
        {/* Sem a seta, o React passa o evento do clique como primeiro argumento,
            que aqui é o número da página — e a paginação exibia "NaN–NaN". */}
        <button className="btn btn-primary" onClick={() => fetchPeriodReport(1)} disabled={loading}>
          <FiSearch /> {loading ? 'Pesquisando...' : 'Pesquisar'}
        </button>
      </div>

      {/* Resultados */}
      {periodReport && (
        <>
          <div className="period-totals">
            <div className="period-total-card">
              <span className="period-total-value">{periodReport.totals.total}</span>
              <span className="period-total-label">Total de registros</span>
            </div>
            <div className="period-total-card period-total-entry">
              <span className="period-total-value">{periodReport.totals.entradas}</span>
              <span className="period-total-label">Entradas</span>
            </div>
            <div className="period-total-card period-total-exit">
              <span className="period-total-value">{periodReport.totals.saidas}</span>
              <span className="period-total-label">Saídas</span>
            </div>
            <div className="period-total-card">
              <span className="period-total-value">{periodReport.totals.com_ticket}</span>
              <span className="period-total-label">Viraram registro</span>
            </div>
            <div className="period-total-card">
              <span className="period-total-value">
                {periodReport.totals.avg_duration === null ? '—' : formatDuration(periodReport.totals.avg_duration)}
              </span>
              <span className="period-total-label">Permanência média</span>
            </div>
          </div>

          {periodRecords.length === 0 ? (
            <div className="yard-empty">
              <FiSearch size={40} style={{ opacity: 0.3 }} />
              <p>Nenhum registro encontrado para os filtros selecionados</p>
            </div>
          ) : (
            <>
              <Pagination
                currentPage={periodPage}
                totalPages={periodTotalPages}
                pageSize={periodPageSize}
                totalRecords={periodTotalRecords}
                onPageChange={(pag) => fetchPeriodReport(pag)}
                onPageSizeChange={(tam) => { setPeriodPageSize(tam); fetchPeriodReport(1, tam); }}
              />

              <table className="report-table">
                <thead>
                  <tr>
                    <th>Data/Hora</th>
                    <th>Placa</th>
                    <th>Movimento</th>
                    <th>Câmera</th>
                    <th>Confiança</th>
                    <th>Permanência</th>
                    <th title="O que o sistema fez com esta leitura">Uso</th>
                    <th title="Evidência fotográfica">📷</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPeriod.map((r, idx) => (
                    <tr key={idx}>
                      <td>{new Date(r.detected_at).toLocaleString('pt-BR')}</td>
                      <td><span className="plate-badge">{r.plate}</span></td>
                      <td>
                        <span className={`movement-badge movement-${r.log_type}`}>
                          {r.log_type === 'entrada' ? '📥 Entrada' : '📤 Saída'}
                        </span>
                      </td>
                      <td>{r.camera_name || '—'}</td>
                      <td>
                        <span className={`confidence-badge ${r.confidence >= 0.8 ? 'conf-high' : r.confidence >= 0.6 ? 'conf-mid' : 'conf-low'}`}>
                          {(r.confidence * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td>{r.duration_minutes ? formatDuration(r.duration_minutes) : '—'}</td>
                      <td>{renderUso(r)}</td>
                      <td className="evidence-cell">
                        {r.log_image_path ? (
                          <button
                            className="btn-evidence"
                            title="Ver evidência fotográfica"
                            onClick={() => setEvidenceModal({
                              url:   evidenceUrl(r.log_image_path),
                              plate: r.plate,
                              type:  r.log_type === 'entrada' ? 'Entrada' : 'Saída'
                            })}
                          >
                            <FiCamera size={15} />
                          </button>
                        ) : (
                          <span className="evidence-none" title="Sem evidência fotográfica">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                currentPage={periodPage}
                totalPages={periodTotalPages}
                pageSize={periodPageSize}
                totalRecords={periodTotalRecords}
                onPageChange={(pag) => fetchPeriodReport(pag)}
                onPageSizeChange={(tam) => { setPeriodPageSize(tam); fetchPeriodReport(1, tam); }}
              />
            </>
          )}
        </>
      )}

      {!periodReport && (
        <div className="yard-empty">
          <FiSearch size={40} style={{ opacity: 0.3 }} />
          <p>Selecione o período e clique em <strong>Pesquisar</strong></p>
        </div>
      )}
    </div>
  );

  // ── Relatório Diário ───────────────────────────────────────────────────────
  // ── Seletor de intervalo, compartilhado pelas abas de período ─────────────
  const IntervaloFiltro = ({ onBuscar }) => (
    <div className="range-filter">
      <div className="filter-group">
        <label htmlFor="rf-ini">De</label>
        <input id="rf-ini" type="date" value={rangeFilters.start_date} max={rangeFilters.end_date}
          onChange={e => setRangeFilters(r => ({ ...r, start_date: e.target.value }))} />
      </div>
      <div className="filter-group">
        <label htmlFor="rf-fim">Até</label>
        <input id="rf-fim" type="date" value={rangeFilters.end_date} min={rangeFilters.start_date} max={today}
          onChange={e => setRangeFilters(r => ({ ...r, end_date: e.target.value }))} />
      </div>
      <div className="range-shortcuts">
        {[['Hoje', 0], ['7 dias', 6], ['30 dias', 29]].map(([rotulo, dias]) => (
          <button key={rotulo} className="btn btn-sm btn-secondary" onClick={() => setRangeFilters({
            start_date: diasAtrasLocal(dias),
            end_date: today,
          })}>{rotulo}</button>
        ))}
      </div>
      {onBuscar && (
        <button className="btn btn-sm btn-primary" onClick={onBuscar} disabled={loading}>
          <FiRefreshCw /> Atualizar
        </button>
      )}
    </div>
  );

  const SemDados = ({ titulo, explicacao }) => (
    <div className="yard-empty">
      <FiSearch size={36} style={{ opacity: 0.25 }} />
      <p><strong>{titulo}</strong></p>
      {explicacao && <p className="empty-hint">{explicacao}</p>}
    </div>
  );

  // ── Relatório Diário ───────────────────────────────────────────────────────
  // "Receita Total" lia `total_revenue`, campo que a API nunca devolveu: o
  // cartão mostrava R$ 0,00 todo dia. "Tickets Finalizados" contava só o status
  // `finalizado` e pela data de ENTRADA — em 06/09 mostrava 2 de 43.
  const renderDailyReport = () => {
    if (!dailyReport) return null;
    const d = dailyReport;
    return (
      <div className="report-content">
        <div className="report-head">
          <h2>Relatório diário</h2>
          <div className="filter-group">
            <label htmlFor="dia">Data</label>
            <input id="dia" type="date" value={selectedDate} max={today}
              onChange={e => setSelectedDate(e.target.value)} />
          </div>
        </div>

        <div className="report-stats">
          <div className="stat-card">
            <h3>Entradas</h3>
            <p className="stat-value">{d.entries}</p>
            <small>{d.tickets_opened} tickets abertos</small>
          </div>
          <div className="stat-card">
            <h3>Saídas lidas</h3>
            <p className="stat-value">{d.exits}</p>
            <small>{d.completed_tickets} tickets encerrados</small>
          </div>
          <div className="stat-card">
            <h3>Permanência média</h3>
            <p className="stat-value">{d.avg_duration_minutes === null ? '—' : formatDuration(d.avg_duration_minutes)}</p>
            <small>
              {d.long_stays > 0
                ? `${d.long_stays} estadia(s) acima de ${d.abandon_window_hours}h fora da média`
                : 'todas as estadias dentro da janela'}
            </small>
          </div>
          <div className="stat-card">
            <h3>Pico de ocupação</h3>
            <p className="stat-value">{d.peak_occupancy}</p>
            <small>
              {d.peak_hour !== null ? `às ${String(d.peak_hour).padStart(2, '0')}h` : 'sem movimento'}
              {d.peak_estimated > 0 && `, ${d.peak_estimated} estimados`}
            </small>
          </div>
          <div className="stat-card">
            <h3>Receita</h3>
            <p className="stat-value">{fmtMoney(d.revenue)}</p>
            <small>{d.payments} pagamento(s)</small>
          </div>
          <div className="stat-card">
            <h3>Ticket médio</h3>
            <p className="stat-value">{d.avg_ticket === null ? '—' : fmtMoney(d.avg_ticket)}</p>
            <small>{d.discounts > 0 ? `${fmtMoney(d.discounts)} em descontos` : 'sem descontos'}</small>
          </div>
        </div>

        {d.peak_estimated > 0 && (
          <p className="report-note">
            O pico de ocupação conta os veículos com ticket em aberto naquela hora. Quando a câmera de
            saída não lê a placa, a saída é estimada pela permanência mediana
            {d.median_stay_minutes ? ` (${formatDuration(d.median_stay_minutes)})` : ''} — desses,
            {' '}<strong>{d.peak_estimated}</strong> entraram por estimativa.
          </p>
        )}
      </div>
    );
  };

  // ── Ocupação ───────────────────────────────────────────────────────────────
  // A coluna "Pico" vinha de `MAX(CASE WHEN status='ativo' THEN 1 ELSE 0 END)`,
  // um booleano: a tela mostrava 0 ou 1 sob o título de pico de ocupação.
  const renderOccupancyReport = () => {
    if (!occupancyReport) return null;
    const linhas = [...(occupancyReport.data || [])].reverse();
    const chartData = {
      labels: linhas.map(d => new Date(d.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })),
      datasets: [
        { label: 'Pico de ocupação', data: linhas.map(d => d.peak_occupancy), backgroundColor: '#14657a', borderRadius: 3 },
        { label: 'Entradas', data: linhas.map(d => d.total_vehicles), backgroundColor: '#8fbecb', borderRadius: 3 },
      ],
    };
    const totalEntradas = linhas.reduce((a, b) => a + b.total_vehicles, 0);
    const totalSaidas = linhas.reduce((a, b) => a + b.exits, 0);
    const totalReceita = linhas.reduce((a, b) => a + Number(b.revenue || 0), 0);

    return (
      <div className="report-content">
        <div className="report-head">
          <h2>Ocupação por dia</h2>
          <IntervaloFiltro onBuscar={() => fetchReport('occupancy')} />
        </div>

        <div className="report-stats">
          <div className="stat-card"><h3>Entradas no período</h3><p className="stat-value">{totalEntradas.toLocaleString('pt-BR')}</p></div>
          <div className="stat-card"><h3>Saídas registradas</h3><p className="stat-value">{totalSaidas.toLocaleString('pt-BR')}</p>
            <small>{totalEntradas > 0 ? `${((totalSaidas / totalEntradas) * 100).toFixed(0)}% das entradas` : '—'}</small></div>
          <div className="stat-card"><h3>Receita no período</h3><p className="stat-value">{fmtMoney(totalReceita)}</p></div>
        </div>

        {linhas.length === 0 ? (
          <SemDados titulo="Nenhum movimento no período escolhido" />
        ) : (
          <>
            <div className="chart-container">
              <Bar data={chartData} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 } } } },
                scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { precision: 0 } } },
              }} />
            </div>

            <table className="report-table">
              <thead>
                <tr>
                  <th>Data</th><th>Entradas</th><th>Saídas</th>
                  <th>Pico de ocupação</th><th>Hora do pico</th>
                  <th>Permanência média</th><th>Receita</th>
                </tr>
              </thead>
              <tbody>
                {(occupancyReport.data || []).map(row => (
                  <tr key={row.date}>
                    <td>{new Date(row.date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })}</td>
                    <td>{row.total_vehicles}</td>
                    <td>{row.exits}</td>
                    <td>
                      {row.peak_occupancy}
                      {row.peak_estimated > 0 && (
                        <span className="hint-inline" title="Saídas estimadas, porque a câmera não leu a placa">
                          {' '}({row.peak_estimated} est.)
                        </span>
                      )}
                    </td>
                    <td>{row.peak_hour !== null ? `${String(row.peak_hour).padStart(2, '0')}h` : '—'}</td>
                    <td>{row.avg_duration === null ? '—' : formatDuration(row.avg_duration)}</td>
                    <td>{fmtMoney(row.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="report-note">
              O pico é o maior número de veículos dentro do pátio em uma hora do dia. Tickets sem leitura de
              saída não ficam abertos para sempre: contam pela permanência mediana observada
              {occupancyReport.median_stay_minutes ? ` (${formatDuration(occupancyReport.median_stay_minutes)})` : ''},
              limitada à Janela de Abandono de {occupancyReport.abandon_window_hours}h. A parte estimada aparece
              entre parênteses.
            </p>
          </>
        )}
      </div>
    );
  };

  // ── Financeiro ─────────────────────────────────────────────────────────────
  const renderFinancialReport = () => {
    if (!financialReport) return null;
    const { tickets = {}, monthly = {}, by_payment_method = [], agreements = [], period = {} } = financialReport;
    const exportCsv = async () => {
      try {
        const response = await api.get(
          `/api/reports/financial/export.csv?start_date=${rangeFilters.start_date}&end_date=${rangeFilters.end_date}`,
          { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `financeiro_${rangeFilters.start_date}_${rangeFilters.end_date}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (_) { toast.error('Não foi possível exportar o CSV financeiro'); }
    };
    const metodoLabel = {
      dinheiro: 'Dinheiro', credito: 'Crédito', debito: 'Débito', pix: 'PIX',
      auto_dinheiro: 'Baixa automática', isento: 'Isento', sem_parar: 'Sem Parar',
    };
    return (
      <div className="report-content">
        <div className="report-head">
          <h2><FiDollarSign /> Financeiro</h2>
          <IntervaloFiltro onBuscar={() => fetchReport('financial')} />
          <button className="btn btn-sm btn-secondary" onClick={exportCsv}><FiDownload /> CSV</button>
        </div>

        <div className="report-stats">
          <div className="stat-card">
            <h3>Receita líquida</h3><p className="stat-value">{fmtMoney(tickets.net_revenue)}</p>
            <small>{tickets.payments || 0} pagamento(s)</small>
          </div>
          <div className="stat-card">
            <h3>Ticket médio</h3><p className="stat-value">{tickets.avg_ticket ? fmtMoney(tickets.avg_ticket) : '—'}</p>
          </div>
          <div className="stat-card">
            <h3>Descontos</h3><p className="stat-value">{fmtMoney(tickets.discounts)}</p>
            <small>sobre {fmtMoney(tickets.gross_revenue)} brutos</small>
          </div>
          <div className="stat-card">
            <h3>Estornos</h3><p className="stat-value">{fmtMoney(tickets.reversed_amount)}</p>
          </div>
          <div className="stat-card">
            <h3>Mensalidades recebidas</h3><p className="stat-value">{fmtMoney(monthly.received)}</p>
            <small>{monthly.invoices_paid || 0} fatura(s)</small>
          </div>
          <div className="stat-card">
            <h3>Carteira em aberto</h3><p className="stat-value">{fmtMoney(monthly.open_balance)}</p>
            <small>{monthly.open_invoices || 0} fatura(s)</small>
          </div>
        </div>

        <h3>Recebimentos por meio de pagamento</h3>
        {by_payment_method.length === 0 ? (
          <SemDados titulo="Nenhum recebimento no período"
            explicacao={`De ${period.start || ''} a ${period.end || ''} não há pagamentos registrados.`} />
        ) : (
          <table className="report-table">
            <thead><tr><th>Meio</th><th>Pagamentos</th><th>Valor líquido</th></tr></thead>
            <tbody>
              {by_payment_method.map(row => (
                <tr key={row.payment_method}>
                  <td>{metodoLabel[row.payment_method] || row.payment_method}</td>
                  <td>{row.payments}</td>
                  <td>{fmtMoney(row.net_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3>Convênios no período</h3>
        {agreements.length === 0 ? (
          <SemDados titulo="Nenhum convênio cadastrado"
            explicacao="Convênios concedem desconto a clientes de estabelecimentos parceiros. Cadastre em Convênios." />
        ) : (
          <table className="report-table">
            <thead><tr><th>Convênio</th><th>Usos</th><th>Descontos concedidos</th><th>Reversões</th></tr></thead>
            <tbody>
              {agreements.map(row => (
                <tr key={row.id}>
                  <td>{row.name}</td><td>{row.usages}</td>
                  <td>{fmtMoney(row.discounts_granted)}</td><td>{row.reversed_usages}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  // ── Caixa ──────────────────────────────────────────────────────────────────
  // A rota respondia 500 desde sempre: fazia `JOIN users u` e lia `u.name`,
  // coluna que não existe na tabela. A tela mostrava uma tabela vazia.
  const renderCashierReport = () => {
    if (!cashierReport) return null;
    const sessoes = cashierReport.sessions || [];
    return (
      <div className="report-content">
        <div className="report-head">
          <h2><FiCreditCard /> Sessões de caixa</h2>
          <IntervaloFiltro onBuscar={() => fetchReport('cashier')} />
        </div>
        {sessoes.length === 0 ? (
          <SemDados
            titulo="Nenhuma sessão de caixa no período"
            explicacao="A operação está cobrando por baixa automática, sem abertura de caixa. Sessões aparecem aqui quando um operador abre o Caixa (PDV)." />
        ) : (
          <table className="report-table">
            <thead>
              <tr>
                <th>Operador</th><th>Abertura</th><th>Situação</th><th>Recebimentos</th>
                <th>Dinheiro</th><th>Cartões</th><th>PIX</th><th>Estornos</th>
              </tr>
            </thead>
            <tbody>
              {sessoes.map(session => (
                <tr key={session.id}>
                  <td>{session.operator_name}</td>
                  <td>{fmtDateTime(session.opened_at)}</td>
                  <td><span className={`status ${session.status === 'aberta' ? 'ativo' : 'finalizado'}`}>{session.status}</span></td>
                  <td>{session.payments} · {fmtMoney(session.collected)}</td>
                  <td>{fmtMoney(session.total_cash)}</td>
                  <td>{fmtMoney(Number(session.total_credit || 0) + Number(session.total_debit || 0))}</td>
                  <td>{fmtMoney(session.total_pix)}</td>
                  <td>{fmtMoney(session.reversed_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  // ── Carteira de mensalidades ───────────────────────────────────────────────
  const renderMonthlyPortfolio = () => {
    if (!monthlyPortfolio) return null;
    const porStatus = monthlyPortfolio.by_status || [];
    const inadimplentes = monthlyPortfolio.delinquent || [];
    const assinaturas = monthlyPortfolio.subscriptions || {};
    const statusLabel = { open: 'Em aberto', partial: 'Parcial', overdue: 'Vencida', paid: 'Paga', cancelled: 'Cancelada' };
    return (
      <div className="report-content">
        <h2><FiCreditCard /> Carteira de mensalidades</h2>
        <div className="report-stats">
          <div className="stat-card">
            <h3>Assinaturas</h3><p className="stat-value">{assinaturas.total || 0}</p>
            <small>{assinaturas.ativas || 0} ativa(s), {assinaturas.suspensas || 0} suspensa(s)</small>
          </div>
          {porStatus.map(item => (
            <div className="stat-card" key={item.status}>
              <h3>{statusLabel[item.status] || item.status}</h3>
              <p className="stat-value">{fmtMoney(item.balance)}</p>
              <small>{item.quantity} fatura(s)</small>
            </div>
          ))}
        </div>

        {porStatus.length === 0 && (
          <SemDados
            titulo="Nenhuma fatura emitida"
            explicacao="A cobrança mensal está instalada, mas o faturamento automático permanece desabilitado até que vencimento, multa, juros e ação de inadimplência sejam definidos em Mensalistas." />
        )}

        {inadimplentes.length > 0 && (
          <>
            <h3>Inadimplência</h3>
            <table className="report-table">
              <thead><tr><th>Cliente</th><th>Plano</th><th>Competência</th><th>Vencimento</th><th>Atraso</th><th>Saldo</th></tr></thead>
              <tbody>
                {inadimplentes.map(invoice => (
                  <tr key={invoice.id}>
                    <td>{invoice.customer_name}</td><td>{invoice.plan_name}</td>
                    <td>{invoice.competency}</td>
                    <td>{new Date(invoice.due_date).toLocaleDateString('pt-BR')}</td>
                    <td>{invoice.days_overdue} dia(s)</td>
                    <td>{fmtMoney(invoice.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  };

  // ── Atividade por câmera ───────────────────────────────────────────────────
  // A coluna de aproveitamento é o que separa uma câmera que lê muito de uma
  // câmera que lê o que importa: desde a Onda 32 cada leitura sabe se abriu ou
  // fechou um ticket.
  const renderCameraReport = () => {
    if (!cameraReport) return null;
    const cameras = cameraReport.cameras || [];
    return (
      <div className="report-content">
        <div className="report-head">
          <h2><FiCamera /> Atividade por câmera</h2>
          <IntervaloFiltro onBuscar={() => fetchReport('camera')} />
        </div>
        {cameras.length === 0 ? (
          <SemDados titulo="Nenhuma leitura no período escolhido" />
        ) : (
          <>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Câmera</th><th>Direção</th><th>Leituras</th>
                  <th>Entradas</th><th>Saídas</th><th>Aproveitadas</th>
                  <th>Confiança média</th><th>Última leitura</th>
                </tr>
              </thead>
              <tbody>
                {cameras.map(camera => (
                  <tr key={camera.id}>
                    <td>
                      {camera.name}
                      {camera.camera_status !== 'ativa' && (
                        <span className="status inativa" style={{ marginLeft: 8 }}>{camera.camera_status}</span>
                      )}
                    </td>
                    <td>{camera.camera_type === 'entrada' ? 'Entrada' : 'Saída'}</td>
                    <td>{camera.total_detections.toLocaleString('pt-BR')}</td>
                    <td>{camera.entries.toLocaleString('pt-BR')}</td>
                    <td>{camera.exits.toLocaleString('pt-BR')}</td>
                    <td>
                      {camera.used_readings.toLocaleString('pt-BR')}
                      {camera.used_pct !== null && (
                        <span className="hint-inline"> ({camera.used_pct.toFixed(0)}%)</span>
                      )}
                    </td>
                    <td>{camera.avg_confidence === null ? '—' : `${(camera.avg_confidence * 100).toFixed(0)}%`}</td>
                    <td>{fmtDateTime(camera.last_reading)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="report-note">
              <strong>Aproveitadas</strong> são as leituras que abriram ou fecharam um ticket. As demais são
              repetições da mesma passagem, veículos fora da zona ou placas que o sistema decidiu não usar —
              a tela Qualidade do OCR mostra o motivo de cada uma.
            </p>
          </>
        )}
      </div>
    );
  };

  // ── Inventário do Pátio — auditoria (Onda 46) ──────────────────────────────
  // A taxa de veículos fantasma é o número que esta aba existe para mostrar:
  // quanto da saída a câmera não está lendo, medido pela única fonte que sabe —
  // alguém que andou pelo pátio e olhou.
  const renderInventoryReport = () => {
    if (!inventoryReport) return null;
    const { summary: r = {}, sessions = [], findings = [], repeat_offenders: reincidentes = [] } = inventoryReport;

    const exportar = async () => {
      try {
        const resp = await api.get(
          `/api/reports/inventory/export.csv?start_date=${rangeFilters.start_date}&end_date=${rangeFilters.end_date}`,
          { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `inventario_${rangeFilters.start_date}_${rangeFilters.end_date}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (_) { toast.error('Não foi possível exportar'); }
    };

    const tratar = async (id, resolution) => {
      try {
        await api.post(`/api/inventory/findings/${id}/resolve`, { resolution });
        toast.success('Pendência tratada');
        fetchReport('inventory');
      } catch (e) {
        toast.error(e.response?.data?.error || 'Não foi possível tratar');
      }
    };

    const grafico = {
      labels: [...sessions].filter(x => x.ghost_rate !== null).reverse()
        .map(x => new Date(x.started_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })),
      datasets: [{
        label: 'Veículos não encontrados (%)',
        data: [...sessions].filter(x => x.ghost_rate !== null).reverse().map(x => Math.round(x.ghost_rate)),
        backgroundColor: '#14657a',
        borderRadius: 3,
      }],
    };

    const statusLabel = {
      finalizado: 'Finalizada', revertido: 'Revertida',
      cancelado: 'Cancelada', em_andamento: 'Em andamento',
    };

    return (
      <div className="report-content">
        <div className="report-head">
          <h2>Inventário do Pátio — auditoria</h2>
          <IntervaloFiltro onBuscar={() => fetchReport('inventory')} />
          <button className="btn btn-sm btn-secondary" onClick={exportar}><FiDownload /> CSV</button>
        </div>

        <div className="report-stats">
          <div className="stat-card">
            <h3>Vistorias no período</h3>
            <p className="stat-value">{r.sessions || 0}</p>
            <small>{r.finished || 0} finalizada(s)</small>
          </div>
          <div className="stat-card">
            <h3>Veículos não encontrados</h3>
            <p className="stat-value">{r.ghost_rate === null || r.ghost_rate === undefined ? '—' : `${r.ghost_rate.toFixed(0)}%`}</p>
            <small>{(r.closed_total || 0).toLocaleString('pt-BR')} de {(r.snapshot_total || 0).toLocaleString('pt-BR')} vistoriados</small>
          </div>
          <div className="stat-card">
            <h3>Encontrados no pátio</h3>
            <p className="stat-value">{(r.found_total || 0).toLocaleString('pt-BR')}</p>
            <small>confirmados pela vistoria</small>
          </div>
          <div className="stat-card">
            <h3>Sem ticket</h3>
            <p className="stat-value">{r.findings_total || 0}</p>
            <small>{r.findings_open || 0} pendente(s) de decisão</small>
          </div>
        </div>

        {sessions.length === 0 ? (
          <SemDados
            titulo="Nenhuma vistoria no período"
            explicacao="O Inventário do Pátio é a vistoria de campo que confirma quais veículos estão de fato no estacionamento. Abra uma pela tela Inventário do Pátio." />
        ) : (
          <>
            {grafico.labels.length > 1 && (
              <>
                <h3>Evolução</h3>
                <div className="chart-container">
                  <Bar data={grafico} options={{
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { grid: { display: false } },
                      y: { beginAtZero: true, max: 100, ticks: { callback: v => `${v}%` } },
                    },
                  }} />
                </div>
                <p className="report-note">
                  Cada barra é uma vistoria: o percentual de veículos que o sistema dava como presentes e
                  não estavam lá. É a medida direta de quanto a câmera de saída deixa de ler — se este
                  número cair ao longo das semanas, o reconhecimento na saída está melhorando.
                </p>
              </>
            )}

            <h3>Vistorias</h3>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Nº</th><th>Início</th><th>Responsável</th><th>Duração</th>
                  <th>No pátio</th><th>Encontrados</th><th>Encerrados</th>
                  <th>Não encontrados</th><th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(x => (
                  <tr key={x.id}>
                    <td>{x.id}</td>
                    <td>{fmtDateTime(x.started_at)}</td>
                    <td>{x.started_by}</td>
                    <td>{x.duration_minutes ? formatDuration(x.duration_minutes) : '—'}</td>
                    <td>{x.snapshot_count}</td>
                    <td>{x.checked_count}</td>
                    <td>{x.closed_count}</td>
                    <td>{x.ghost_rate === null ? '—' : `${x.ghost_rate.toFixed(0)}%`}</td>
                    <td>
                      <span className={`status ${x.status === 'finalizado' ? 'ativo' : 'finalizado'}`}>
                        {statusLabel[x.status] || x.status}
                      </span>
                      {x.reverted_by && <span className="hint-inline"> por {x.reverted_by}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {findings.length > 0 && (
              <>
                <h3>Veículos encontrados sem ticket</h3>
                <table className="report-table">
                  <thead>
                    <tr><th>Placa</th><th>Vistoria</th><th>Registrado em</th><th>Por</th><th>Observação</th><th>Decisão</th></tr>
                  </thead>
                  <tbody>
                    {findings.map(f => (
                      <tr key={f.id}>
                        <td><span className="plate-badge">{f.plate}</span></td>
                        <td>{f.session_id}</td>
                        <td>{fmtDateTime(f.reported_at)}</td>
                        <td>{f.reported_by}</td>
                        <td>{f.notes || '—'}</td>
                        <td className="cell-actions">
                          {(!f.resolution || f.resolution === 'pendente') ? (
                            <>
                              <button className="btn btn-sm btn-info" onClick={() => tratar(f.id, 'ticket_criado')}>
                                Ticket criado
                              </button>
                              <button className="btn btn-sm btn-secondary" onClick={() => tratar(f.id, 'ignorado')}>
                                Ignorar
                              </button>
                            </>
                          ) : (
                            <span className="hint-inline">
                              {f.resolution === 'ticket_criado' ? 'ticket criado' : 'ignorado'}
                              {f.resolved_by ? ` · ${f.resolved_by}` : ''}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="report-note">
                  Um veículo no pátio sem ticket indica entrada que a câmera não leu. O sistema não cria
                  o ticket sozinho: não há como saber a que horas ele entrou, e arbitrar esse horário
                  seria inventar cobrança.
                </p>
              </>
            )}

            {reincidentes.length > 0 && (
              <>
                <h3>Placas encerradas mais de uma vez</h3>
                <table className="report-table">
                  <thead><tr><th>Placa</th><th>Vezes</th><th>Último encerramento</th></tr></thead>
                  <tbody>
                    {reincidentes.map(x => (
                      <tr key={x.plate}>
                        <td><span className="plate-badge">{x.plate}</span></td>
                        <td>{x.vezes}</td>
                        <td>{fmtDateTime(x.ultima)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="report-note">
                  O mesmo veículo entrou, sumiu do pátio e foi encerrado mais de uma vez no período. É o
                  padrão de quem usa o estacionamento sem passar pelo caixa — vale cruzar com a
                  Quarentena e com a Frequência por Veículo.
                </p>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  // ── Frequência por Veículo (Onda 40) ───────────────────────────────────────
  // Veículos com pelo menos N estadias no período. A paginação é do servidor:
  // trinta dias podem render milhares de placas, e trazer tudo para paginar no
  // navegador é o que trava as outras abas.
  const renderFrequencyReport = () => {
    const items = frequencyReport?.items || [];
    const resumo = frequencyReport?.summary || {};
    const total = frequencyReport?.total || 0;
    const paginas = Math.max(1, Math.ceil(total / freqFilters.limit));
    const paginaAtual = Math.floor(freqFilters.offset / freqFilters.limit) + 1;
    const irPara = (pagina) => {
      const alvo = Math.min(paginas, Math.max(1, pagina));
      const filtros = { ...freqFilters, offset: (alvo - 1) * freqFilters.limit };
      setFreqFilters(filtros);
      fetchFrequencyReport(filtros);
    };

    return (
      <div className="report-content">
        <div className="period-filters">
          <div className="filter-group">
            <label>Data inicial</label>
            <input type="date" value={freqFilters.start_date}
              onChange={e => setFreqFilters({ ...freqFilters, start_date: e.target.value })} />
          </div>
          <div className="filter-group">
            <label>Data final</label>
            <input type="date" value={freqFilters.end_date}
              onChange={e => setFreqFilters({ ...freqFilters, end_date: e.target.value })} />
          </div>
          <div className="filter-group">
            <label>Contar por</label>
            <select value={freqFilters.base}
              onChange={e => setFreqFilters({ ...freqFilters, base: e.target.value })}>
              <option value="acessos">Estadias</option>
              <option value="dias">Dias com acesso</option>
            </select>
          </div>
          <div className="filter-group">
            <label>{freqFilters.base === 'dias' ? 'Mínimo de dias com acesso' : 'Mínimo de estadias'}</label>
            <input type="number" min="1" max="500" value={freqFilters.min_visits}
              onChange={e => setFreqFilters({ ...freqFilters, min_visits: Math.max(1, Number(e.target.value) || 1) })} />
          </div>
          <div className="filter-group">
            <label>Categoria</label>
            <select value={freqFilters.vehicle_type}
              onChange={e => setFreqFilters({ ...freqFilters, vehicle_type: e.target.value })}>
              <option value="">Todas</option>
              <option value="avulso">Avulso</option>
              <option value="mensalista">Mensalista</option>
              <option value="credenciado">Credenciado</option>
              <option value="isento">Isento</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Linhas por página</label>
            <select value={freqFilters.limit}
              onChange={e => setFreqFilters({ ...freqFilters, limit: Number(e.target.value), offset: 0 })}>
              {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" disabled={loading}
            onClick={() => { const f = { ...freqFilters, offset: 0 }; setFreqFilters(f); fetchFrequencyReport(f); }}>
            {loading ? 'Buscando…' : 'Pesquisar'}
          </button>
          {items.length > 0 && (
            <button className="btn btn-secondary" onClick={exportFrequencyCsv}>Exportar CSV</button>
          )}
        </div>

        {!frequencyReport && !loading && (
          <div className="yard-empty">
            Escolha o período, o que contar e o mínimo, depois clique em Pesquisar.
          </div>
        )}

        {frequencyReport && (
          <>
            <div className="period-totals">
              <div className="period-total-card"><span>Placas</span><strong>{resumo.placas || 0}</strong></div>
              <div className="period-total-card"><span>Estadias na página</span><strong>{resumo.visitas || 0}</strong></div>
              <div className="period-total-card"><span>Receita na página</span><strong>{fmtMoney(resumo.receita)}</strong></div>
              <div className="period-total-card"><span>Sem saída lida</span><strong>{resumo.sem_saida || 0}</strong></div>
              <div className="period-total-card"><span>Em quarentena</span><strong>{resumo.em_quarentena || 0}</strong></div>
            </div>

            <div className="report-table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Placa</th><th>Estadias</th><th>Dias com acesso</th><th>Primeira</th><th>Última</th>
                    <th>Permanência total</th><th>Média</th><th>Receita</th><th>Média/estadia</th>
                    <th>Sem saída</th><th>Categoria</th><th>Quarentena</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr><td colSpan={12} className="yard-empty">Nenhum veículo atingiu o mínimo no período.</td></tr>
                  )}
                  {items.map(v => (
                    <tr key={v.plate} className={v.quarantine ? 'row-warning' : ''}>
                      <td><span className="plate-badge">{v.plate}</span></td>
                      <td><strong>{v.visits}</strong></td>
                      <td><strong>{v.days ?? '—'}</strong></td>
                      <td>{fmtDateTime(v.first_entry)}</td>
                      <td>{fmtDateTime(v.last_entry)}</td>
                      <td>{fmtMinutes(v.total_minutes)}</td>
                      <td>{fmtMinutes(v.avg_minutes)}</td>
                      <td>{fmtMoney(v.total_amount)}</td>
                      <td>{fmtMoney(v.avg_amount)}</td>
                      <td className={v.without_exit > 0 ? 'conf-low' : ''}>
                        {v.without_exit > 0 ? `${v.without_exit} de ${v.visits}` : '—'}
                      </td>
                      <td>{v.vehicle_types.join(', ') || '—'}</td>
                      <td>
                        {v.quarantine
                          ? <span className="movement-badge" title={`Status: ${v.quarantine.status}`}>
                              {v.quarantine.occurrences} ocorrência(s){v.quarantine.carona_occurrences > 0 ? `, ${v.quarantine.carona_occurrences} com carona` : ''}
                            </span>
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {paginas > 1 && (
              <div className="pagination-bar">
                <button className="btn btn-secondary btn-sm" disabled={paginaAtual <= 1} onClick={() => irPara(paginaAtual - 1)}>Anterior</button>
                <span>Página {paginaAtual} de {paginas} · {total} placa(s)</span>
                <button className="btn btn-secondary btn-sm" disabled={paginaAtual >= paginas} onClick={() => irPara(paginaAtual + 1)}>Próxima</button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
    <div className="reports-page">
      <h1>Relatórios</h1>

      <div className="report-selector">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`report-btn ${reportType === tab.id ? 'active' : ''}`}
            onClick={() => setReportType(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && reportType !== 'yard' && reportType !== 'period' && reportType !== 'frequency' ? (
        <div className="loading">Gerando relatório...</div>
      ) : (
        <>
          {reportType === 'yard'      && renderYardReport()}
          {reportType === 'period'    && renderPeriodReport()}
          {reportType === 'daily'     && renderDailyReport()}
          {reportType === 'occupancy' && renderOccupancyReport()}
          {reportType === 'camera'    && renderCameraReport()}
          {reportType === 'financial' && renderFinancialReport()}
          {reportType === 'cashier'   && renderCashierReport()}
          {reportType === 'monthly'   && renderMonthlyPortfolio()}
          {reportType === 'frequency' && renderFrequencyReport()}
          {reportType === 'inventory' && renderInventoryReport()}
        </>
      )}
    </div>

    {/* Modal de evidência fotográfica */}
    {evidenceModal && (
      <div className="evidence-modal-overlay" onClick={() => setEvidenceModal(null)}>
        <div className="evidence-modal" onClick={e => e.stopPropagation()}>
          <div className="evidence-modal-header">
            <span>📷 Evidência — {evidenceModal.plate} ({evidenceModal.type})</span>
            <button className="evidence-modal-close" onClick={() => setEvidenceModal(null)}>✕</button>
          </div>
          <img
            src={evidenceModal.url}
            alt={`Evidência ${evidenceModal.type} — ${evidenceModal.plate}`}
            className="evidence-modal-img"
            onError={e => { e.target.src = ''; e.target.alt = 'Imagem não disponível'; }}
          />
          <div className="evidence-modal-caption">
            Clique fora da imagem para fechar
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Reports;
